import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/lib/auth";
import { useLocale } from "@/lib/locale";
import { fetchThread, sendMessage } from "@/lib/api";
import { Loader2, ArrowLeft, Send } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function MessageThread() {
  const { user_id: otherId } = useParams();
  const { user } = useAuth() || {};
  const { t } = useLocale();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const load = () => fetchThread(otherId).then(setData);
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [otherId, user]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data?.messages?.length]);

  if (!user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber" /></div>;

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await sendMessage(otherId, text);
      setText("");
      load();
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="max-w-3xl w-full mx-auto px-6 py-8 flex-1 flex flex-col" data-testid="thread-page">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/messages" className="text-zinc-400 hover:text-amber" data-testid="thread-back">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          {data.other.picture ? (
            <img src={data.other.picture} alt="" className="h-10 w-10 rounded-full object-cover border border-white/10" />
          ) : (
            <div className="h-10 w-10 rounded-full bg-amber/20 text-amber font-heading flex items-center justify-center">{data.other.name?.[0]}</div>
          )}
          <div className="font-display text-2xl text-white">{data.other.name}</div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-3 mb-4" data-testid="messages-list">
          {data.messages.length === 0 && (
            <p className="text-center text-zinc-500 font-body italic">No messages yet — say hello.</p>
          )}
          {data.messages.map((m) => {
            const mine = m.from_user_id === user.user_id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                <div className={`max-w-[75%] px-4 py-2 rounded-md border ${
                  mine ? "bg-amber/20 border-amber/40 text-zinc-100" : "bg-obsidian-surface border-white/10 text-zinc-200"
                }`}>
                  <p className="font-body text-sm whitespace-pre-wrap break-words">{m.body}</p>
                  <div className="text-[10px] text-zinc-500 mt-1 text-right font-heading">{new Date(m.created_at).toLocaleString()}</div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        <div className="glass rounded-md p-3 flex items-center gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
            placeholder={t("new_message")}
            className="flex-1 bg-transparent outline-none text-zinc-200 placeholder:text-zinc-600 font-body"
            data-testid="message-input"
          />
          <Button disabled={busy || !text.trim()} onClick={submit} className="bg-amber hover:bg-amber-hover text-obsidian" data-testid="message-send">
            <Send className="h-4 w-4 mr-1" /> {t("send")}
          </Button>
        </div>
      </main>
    </div>
  );
}
